/**
 * How the ground looks: rock and its layers, the ores, and the ground covers,
 * in the plants' natural look (otherworld and woodcut ground were tried and
 * dropped). TileAtlas paints every ground block from here (GROUND_PAINT).
 *
 * A stone ground is rounded stones packed with dark seams (stoneField); an
 * ore is lumps set in its stone; a cover is a band of turf along the tile's
 * top over stone (the atlas turns natural tiles to face out, so the top is
 * the open side).
 */
import type { BlockKey } from "@dyson/shared";
import { ROCK, S, TEAL, flip, hash, noise, pixels, stoneAt, stoneField, tone, type Ctx, type PixelFn } from "./paint";

type Ramp = readonly string[];
type Lump = readonly [number, number, number, number];
type LumpPal = readonly [string, string, string, string];

/** Rounded stones for variant `v`. */
function stoneOf(ramp: Ramp, seed: number, v: number, soft?: Parameters<typeof stoneField>[3]): PixelFn {
  return stoneField(ramp, 8, seed + v * 53, soft);
}

/** Rounded mineral lumps in the stone, a glint on each. */
function lumps(v: number, list: readonly Lump[], pal: LumpPal): PixelFn {
  const placed = list.map(([x, y, rx, ry]) => [...flip(v, x, y), rx, ry] as const);
  return (x, y) => {
    for (const [cx, cy, rx, ry] of placed) {
      const dx = (x + 0.5 - cx) / rx;
      const dy = (y + 0.5 - cy) / ry;
      const d = dx * dx + dy * dy + (hash(x, y, 3) - 0.5) * 0.3;
      if (d > 1) continue;
      if (d > 0.6) return pal[0];
      if (dx + dy < -0.85) return pal[3];
      return dx + dy < -0.3 ? pal[2] : pal[1];
    }
    return null;
  };
}

/** Ground cover: a band of turf along the top over stone, a few bits (flowers, embers) in it. */
function coverOf(turf: Ramp, bits: Ramp, ground: Ramp, seed: number, v: number): PixelFn {
  const rock = stoneOf(ground, seed, v);
  return (x0, y) => {
    const x = v % 2 ? S - 1 - x0 : x0;
    const depth = 6 + Math.floor(noise(x, 0, 4, seed + 1) * 5);
    if (y < depth) {
      if (y === depth - 1) return turf[0]!;
      if (y >= 1 && y <= depth - 3 && Math.abs(x - 15.5) > 3 && hash(x, y, seed + v + 4) < 0.025) return bits[Math.floor(hash(x, y, seed + 5) * bits.length)]!;
      let k = 0.78 - (y / depth) * 0.5 + (noise(x, y, 4, seed + 2) - 0.5) * 0.3;
      if (y === 0 && hash(x, 0, seed + 3) < 0.35) k += 0.35;
      return tone(turf, k, x, y);
    }
    if (y === depth) return ground[1]!;
    if (y < depth + 4 && hash(x >> 1, 0, seed + 6) < 0.18) return turf[1]!;
    return rock(x0, y);
  };
}

const RAMPS = {
  ore: ["#382f27", "#4e443a", "#62564a", "#76695b", "#8a7c6c"],
  coal: ["#332c25", "#463d34", "#5c5246", "#6e6355", "#807464"],
  regolith: ["#5e5244", "#77695a", "#8e7f6a", "#a6967f", "#bcae96"],
  deep: ["#2a2420", "#3a332c", "#4a4138", "#574d43", "#655a4f"],
  sunglass: ["#22180f", "#2e211c", "#3b2b26", "#4a362e", "#5a3b2e"],
  ember: ["#1f1612", "#2c201b", "#3a2a24", "#4a362e", "#5a443a"],
  ice: ["#5f93b8", "#8fbad2", "#b4d6e8", "#d7ecf6", "#f2fbff"],
  frostRock: ["#4f5a63", "#5f6b75", "#6f7c86", "#7f8c96", "#909ca6"],
  ashRock: ["#2f2a24", "#3f382f", "#4f463b", "#5a4f42", "#685c4e"],
  clay: ["#4a3a2e", "#6e5644", "#8a6e56", "#a4866a", "#b89a7c"],
  sand: ["#7a6a44", "#a08c5c", "#bca878", "#d2c08e", "#e6d8a8"],
  obsidian: ["#07060a", "#121018", "#1e1a28", "#2c2638", "#4a4060"],
  barren: ["#4a4238", "#5e554a", "#716759", "#857a6a", "#978c7c"],
} as const;

// ----- grounds that are not stone fields

/** Sand: wind ripples across it, a lighter crest on each, fine grit, no stones. */
function sand(v: number): PixelFn {
  const ramp = RAMPS.sand;
  return (x, y) => {
    const phase = (y + noise(x, y, 10, 211 + v) * 6 + x * 0.12) * (Math.PI / 4.5);
    const r = Math.sin(phase);
    let k = 0.5 + r * 0.22 + (hash(x, y, 212 + v) - 0.5) * 0.22;
    if (r > 0.8) k += 0.25;
    if (y < 3) k += 0.2;
    return tone(ramp, k, x, y, 0.8);
  };
}

/** Obsidian: glassy black stones, each with a bright curved break catching violet light. */
function obsidian(v: number): PixelFn {
  const ramp = RAMPS.obsidian;
  return (x, y) => {
    const s = stoneAt(x, y, 10, 205 + v * 53);
    if (s.edge < 1) return ramp[0]!;
    // The conchoidal break: a bright arc inside the upper-left edge, and a second fainter one further in.
    const d = s.dx + s.dy;
    if (s.edge > 1.8 && s.edge < 3 && d < -0.3) return ramp[4]!;
    if (s.edge > 4.5 && s.edge < 5.5 && d < -0.5) return ramp[3]!;
    return tone(ramp, 0.28 + s.tone * 0.25 - d * 0.12, x, y, 0.3);
  };
}

/** Salt crust: a cracked white crust in polygons, tinted tan where it is thin, over dry barren stone; sparkles on the crust. */
function salt(v: number): PixelFn {
  const crust = ["#8a7f68", "#b8ad94", "#d6ccb6", "#e8e0cf", "#f4efe4"] as const;
  const rock = stoneOf(RAMPS.barren, 209, v);
  return (x0, y) => {
    const x = v % 2 ? S - 1 - x0 : x0;
    const depth = 7 + Math.floor(noise(x, 0, 6, 210) * 3);
    if (y >= depth) {
      if (y < depth + 3 && hash(x >> 1, 0, 213) < 0.25) return crust[1]!;
      return rock(x0, y);
    }
    const s = stoneAt(x, y, 10, 214 + v);
    if (s.edge < 0.9) return crust[0]!;
    if (hash(x, y, 215 + v) < 0.03 && y > 0) return "#ffffff";
    return tone(crust, 0.55 + (s.tone - 0.5) * 0.3 - (y / depth) * 0.3 + (y === 0 ? 0.3 : 0), x, y, 0.4);
  };
}

/** Mycelium: a felt of pale lilac threads over dark stone, glowing teal in patches, threads reaching down into the rock. */
function mycelium(v: number): PixelFn {
  const felt = ["#2a2634", "#3e3850", "#56506a", "#6e6884", "#8a84a0", "#b0aac4"] as const;
  const rock = stoneOf(RAMPS.deep, 207, v);
  return (x0, y) => {
    const x = v % 2 ? S - 1 - x0 : x0;
    const depth = 6 + Math.floor(noise(x, 0, 4, 216) * 4);
    const glowHere = noise(x, y, 8, 217 + v) > 0.68;
    if (y < depth) {
      // Threads: short vertical runs of a lighter tone, with the glow where the felt is thick.
      const thread = hash(x >> 1, (y + x) >> 2, 218 + v) < 0.3;
      if (glowHere && hash(x, y, 219) < 0.35) return hash(x, y, 220) < 0.3 ? "#d8fff4" : TEAL[5]!;
      let k = 0.5 - (y / depth) * 0.35 + (thread ? 0.3 : 0);
      if (y === 0) k += 0.25;
      return tone(felt, k, x, y, 0.3);
    }
    // Threads and glowing root-tips reaching into the stone below.
    if (y < depth + 8 && hash(x, 0, 221 + v) < 0.22 && (y - depth) < 3 + hash(x, 1, 222 + v) * 6) {
      return (y - depth) > 2 + hash(x, 1, 222 + v) * 6 - 1 && hash(x, 2, 223) < 0.5 ? TEAL[4]! : felt[4]!;
    }
    return rock(x0, y);
  };
}

// ----- every ground

type Ground = { stone: Ramp; soft?: Parameters<typeof stoneField>[3] } | { ore: Ramp; lumps: readonly Lump[]; pal: LumpPal } | { cover: Ramp; bits: Ramp; ground: Ramp } | { custom: (v: number) => PixelFn };

/** Each ground block: how it's drawn, and the seed its stones are laid from. */
const GROUNDS: Partial<Record<BlockKey, Ground & { seed: number }>> = {
  ROCK: { stone: ROCK, seed: 7 },
  // Loose, gritty, barely seamed: rubble more than stone.
  REGOLITH: { stone: RAMPS.regolith, soft: { seam: 0.6, grit: 0.55, relief: 0.12 }, seed: 61 },
  // Big dark stones, tight seams, almost no grit.
  DEEP_ROCK: { stone: RAMPS.deep, soft: { seam: 1.4, grit: 0.1, relief: 0.3 }, seed: 67 },
  // Rounded rusty lumps set in the stone, a glint on each.
  ORE: { ore: RAMPS.ore, lumps: [[7, 8, 4, 3], [22, 5, 3.5, 2.5], [15, 18, 4.5, 3.5], [26, 25, 3.5, 3], [5, 26, 3, 2.5]], pal: ["#4a2410", "#c27a3a", "#e0a060", "#ffe0b0"], seed: 11 },
  // Dull black lumps with one cold glint each.
  COAL: { ore: RAMPS.coal, lumps: [[6, 7, 5, 3.5], [21, 5, 4, 3], [14, 18, 6, 4], [26, 23, 4, 3.5], [5, 26, 4, 3]], pal: ["#0a0908", "#1b1918", "#34302c", "#9a9aa8"], seed: 23 },
  // Pale blue-white, rounded, nearly seamless.
  ICE: { stone: RAMPS.ice, soft: { seam: 0.5, grit: 0.05, relief: 0.35 }, seed: 141 },
  // Pools of amber glass set in scorched stone.
  SUNGLASS: { ore: RAMPS.sunglass, lumps: [[9, 7, 6, 2.5], [22, 19, 5, 3], [8, 25, 3.5, 2]], pal: ["#5a2a12", "#c98a4a", "#f2c27a", "#fff4d0"], seed: 71 },
  // Glowing orange lumps; the renderer blooms them (GLOW).
  EMBER_ORE: { ore: RAMPS.ember, lumps: [[7, 7, 3.5, 3], [23, 8, 3.5, 3], [12, 21, 4, 3], [25, 25, 3, 2.5]], pal: ["#6a1a04", "#ff7a2a", "#ffb050", "#fff0b8"], seed: 73 },
  GRASS: { cover: ["#1c3a16", "#2f5e24", "#3d742c", "#4f8a3a", "#66a548", "#8cc860"], bits: ["#e8d85a", "#f2f2e6", "#d87ab0"], ground: ROCK, seed: 7 },
  MOSS: { cover: ["#1e2e18", "#2f4a2a", "#3c5a32", "#46613a", "#5c7d48", "#76964f"], bits: ["#a8c060", "#c8d870"], ground: ROCK, seed: 154 },
  LICHEN: { cover: ["#4a4830", "#6b6a44", "#7d7a4f", "#8d8a5a", "#a7a46c", "#c8c488"], bits: ["#c8b45a", "#d68a4a", "#e0e0d0"], ground: ROCK, seed: 155 },
  FROST: { cover: ["#8fbad2", "#a8d2e6", "#c8e6f2", "#dcf0f8", "#eef8fc", "#ffffff"], bits: ["#ffffff", "#bfe6ff"], ground: RAMPS.frostRock, seed: 156 },
  ASH: { cover: ["#2a2624", "#3f3a37", "#4f4945", "#5a5450", "#6e6864", "#86807a"], bits: ["#ff7a2a", "#2f2b29"], ground: RAMPS.ashRock, seed: 157 },
  // Smooth, soft-edged, barely seamed; a damp sheen on the upper faces.
  CLAY: { stone: RAMPS.clay, soft: { seam: 0.4, grit: 0.05, relief: 0.08 }, seed: 201 },
  SAND: { custom: sand, seed: 203 },
  OBSIDIAN: { custom: obsidian, seed: 205 },
  MYCELIUM: { custom: mycelium, seed: 207 },
  SALT: { custom: salt, seed: 209 },
};

/** Every ground block's painter, by its name in the registry. */
export const GROUND_PAINT: Readonly<Record<string, (ctx: Ctx, v: number) => void>> = Object.fromEntries(
  Object.entries(GROUNDS).map(([key, g]) => [
    key,
    (ctx: Ctx, v: number) => {
      if ("custom" in g) pixels(ctx, S, S, g.custom(v));
      else if ("stone" in g) pixels(ctx, S, S, stoneOf(g.stone, g.seed, v, g.soft));
      else if ("ore" in g) {
        const rock = stoneOf(g.ore, g.seed, v);
        const ore = lumps(v, g.lumps, g.pal);
        pixels(ctx, S, S, (x, y) => ore(x, y) ?? rock(x, y));
      } else pixels(ctx, S, S, coverOf(g.cover, g.bits, g.ground, g.seed, v));
    },
  ]),
);
